// Valeurs par défaut des nouvelles études, tenues dans le catalogue du matériel (format 15, 30/10/2026) :
// modifiables par le commercial dans chaque étude ; un réglage qui s'en écarte est signalé en Step 2 et en Step 4
// (« défaut : X ↺ ») et récapitulé dans le rapport (Step 6, écran seulement).
import Vue from 'vue'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'
import { buildInitialState } from '@/store/modules/wizard.module'
import { estModifie } from '@/utils/estModifie'

export interface Parametre {
  cle: string
  libelle: string
  groupe: string
  etape: number
  unite?: string
  options?: { valeur: string, libelle: string }[]
  // réglage pris en compte dans l'étude (ex. apport seulement avec financement)
  si?: (s: any) => boolean
}

export const TYPES_POSE = [
  { valeur: 'roof', libelle: 'Surimposition (toiture inclinée ou plate)' },
  { valeur: 'free', libelle: 'Au sol ou ombrière' },
  { valeur: 'building', libelle: 'Intégré au bâti sur toiture inclinée (BIPV)' },
]

export const PARAMETRES_ETUDE: Parametre[] = [
  { cle: 'pertesSysteme', libelle: 'Pertes du système', unite: '%', groupe: 'Production', etape: 2, si: s => s.modeleProduction === 'pvgis' },
  { cle: 'mountingplace', libelle: 'Type de pose', groupe: 'Production', etape: 2, options: TYPES_POSE },
  { cle: 'batRangeMin', libelle: 'Plage de batteries : minimum', unite: 'kWh', groupe: 'Batterie', etape: 4 },
  { cle: 'batRangeMax', libelle: 'Plage de batteries : maximum', unite: 'kWh', groupe: 'Batterie', etape: 4 },
  { cle: 'rendementBatterie', libelle: 'Rendement de la batterie', unite: '%', groupe: 'Batterie', etape: 4 },
  { cle: 'rendementConversionBatterie', libelle: 'Rendement de conversion à la décharge', unite: '%', groupe: 'Batterie', etape: 4 },
  { cle: 'capaciteUtilisableBatterie', libelle: 'Capacité de batterie utilisée', unite: '%', groupe: 'Batterie', etape: 4 },
  { cle: 'inflationElec', libelle: "Inflation de l'électricité", unite: '%/an', groupe: 'Projections', etape: 4 },
  { cle: 'degradationPanneaux', libelle: 'Dégradation des panneaux', unite: '%/an', groupe: 'Projections', etape: 4 },
  { cle: 'degradationBatterie', libelle: 'Dégradation de la batterie', unite: '%/an', groupe: 'Projections', etape: 4 },
  { cle: 'zeroInjection', libelle: 'Zéro-injection (pas de vente du surplus)', groupe: 'Valorisation', etape: 4 },
  { cle: 'rachatSurplusUnitaire', libelle: 'Rachat du surplus', unite: '€/kWh', groupe: 'Valorisation', etape: 4, si: s => !s.zeroInjection },
  { cle: 'avecMaintenance', libelle: 'Maintenance comptée', groupe: 'OPEX', etape: 4 },
  { cle: 'hausseMaintenancePct', libelle: 'Hausse de la maintenance', unite: '%', groupe: 'OPEX', etape: 4, si: s => s.avecMaintenance },
  { cle: 'hausseMaintenancePeriode', libelle: 'Hausse de la maintenance : tous les', unite: 'ans', groupe: 'OPEX', etape: 4, si: s => s.avecMaintenance },
  { cle: 'avecAssurance', libelle: 'Assurance comptée', groupe: 'OPEX', etape: 4 },
  { cle: 'inflationAssurance', libelle: "Inflation de l'assurance", unite: '%/an', groupe: 'OPEX', etape: 4, si: s => s.avecAssurance },
  { cle: 'avecFinancement', libelle: 'Simuler avec financement', groupe: 'Financement', etape: 4 },
  { cle: 'apportPct', libelle: 'Apport', unite: '%', groupe: 'Financement', etape: 4, si: s => s.avecFinancement },
  { cle: 'tauxEmprunt', libelle: "Taux de l'emprunt", unite: '%', groupe: 'Financement', etape: 4, si: s => s.avecFinancement },
  { cle: 'dureeEmprunt', libelle: "Durée de l'emprunt", unite: 'ans', groupe: 'Financement', etape: 4, si: s => s.avecFinancement },
  { cle: 'dureeEmpruntAuPlusCourt', libelle: 'Durée au plus court', groupe: 'Financement', etape: 4, si: s => s.avecFinancement },
]

// choix du catalogue pour les études (GET /wizard/catalogue-materiel : sans prix d'achat), chargés une fois
export const catalogueEtude = Vue.observable({ choix: null as any })
let chargement: Promise<any> | null = null

export function chargerCatalogueEtude(): Promise<any> {
  if (!chargement) {
    // dans la chaîne de promesses : une erreur (non connecté, réseau) laisse les valeurs de RETIX
    chargement = Promise.resolve()
      .then(() => axios.get(conf.API_URL + '/wizard/catalogue-materiel',
        { headers: { Authorization: 'Bearer ' + authenticationService.status.jwtToken } }))
      .then((res: any) => { catalogueEtude.choix = (res && res.data && res.data.data) || null; return catalogueEtude.choix })
      .catch(() => { chargement = null; return null }) // réessayé au prochain appel
  }
  return chargement
}

/** Valeurs par défaut : celles du catalogue, sinon celles de RETIX (catalogue pas encore chargé). */
export function defautsEtude(): any {
  const d: any = {}
  const initial: any = buildInitialState()
  const cat = catalogueEtude.choix && catalogueEtude.choix.defautsEtudes
  PARAMETRES_ETUDE.forEach(p => { d[p.cle] = cat && p.cle in cat ? cat[p.cle] : initial[p.cle] })
  return d
}

export function texteValeur(p: Parametre | null, v: any, unite?: string): string {
  if (typeof v === 'boolean') return v ? 'activé' : 'désactivé'
  if (v === null || v === undefined || v === '') return 'vide'
  if (p && p.options) {
    const o = p.options.find(x => x.valeur === v)
    return o ? o.libelle : String(v)
  }
  const n = Number(v)
  if (isNaN(n)) return String(v)
  const u = unite !== undefined ? unite : (p && p.unite) || ''
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 3 }).replace(/ | /g, ' ') + (u ? ' ' + u : '')
}

export interface Ecart { libelle: string, valeur: string, defaut: string, etape: number }

/** Sections du CAPEX qui ont une marge (03/11/2026) : clé de la marge du commercial et de la marge du catalogue. */
export const SECTIONS_MARGE = [
  { cle: 'margeMaterielPct', section: 'PV', catalogue: 'pv', libelle: "Marge de l'installation PV" },
  { cle: 'margeBatteriePct', section: 'BATTERIE', catalogue: 'batterie', libelle: 'Marge de la batterie' },
  { cle: 'margePrestaPct', section: 'PRESTA', catalogue: 'presta', libelle: 'Marge des prestations externes' },
  { cle: 'margeBornesPct', section: 'BORNES', catalogue: 'bornes', libelle: 'Marge des bornes de charge' },
]

/**
 * Marges minimales du catalogue (03/11/2026) pour le type de client (particulier, agricole ou ICT) et la puissance :
 * { pv, batterie, presta, bornes } ; tranche à borne haute incluse, au-delà la dernière.
 */
export function margesMinimales(s: any, kwc?: number): any {
  const c = catalogueEtude.choix
  if (!c || !c.marges) return null
  const l = s.clientType === 'Particulier' ? c.marges.particulier : (s.materiel && s.materiel.typePro === 'agri' ? c.marges.agri : c.marges.ict)
  if (!l || !l.length) return null
  const p = kwc != null ? kwc : 0
  return l.find((t: any) => t.kwcMax == null || p <= t.kwcMax + 1e-9) || l[l.length - 1]
}

/** Réglages de l'étude qui s'écartent des valeurs par défaut du catalogue (récapitulatif de la Step 6). */
export function ecartsAuxDefauts(s: any): Ecart[] {
  const d = defautsEtude()
  const ecarts: Ecart[] = []
  PARAMETRES_ETUDE.forEach(p => {
    if (p.si && !p.si(s)) return
    // type de pose donné par la typologie de chaque toiture depuis le 03/11/2026 : plus un réglage
    if (p.cle === 'mountingplace' && (s.typologie || (Array.isArray(s.toitures) && s.toitures.some((t: any) => t.typologie)))) return
    if (p.cle === 'mountingplace' && s.modeleProduction === 'pvgis' && Array.isArray(s.toitures) && s.toitures.length) {
      s.toitures.forEach((t: any, i: number) => {
        // type de pose choisi seulement sur une toiture inclinée (03/11/2026) ; les autres typologies ont le leur
        if ((!t.typologie || t.typologie === 'inclinee') && estModifie(t.mountingplace, d.mountingplace)) {
          ecarts.push({ libelle: p.libelle + (s.toitures.length > 1 ? ' (toiture ' + (i + 1) + ')' : ''), valeur: texteValeur(p, t.mountingplace), defaut: texteValeur(p, d.mountingplace), etape: 2 })
        }
      })
      return
    }
    if (p.cle === 'mountingplace' && s.typologie && s.typologie !== 'inclinee') return
    if (estModifie(s[p.cle], d[p.cle])) ecarts.push({ libelle: p.libelle, valeur: texteValeur(p, s[p.cle]), defaut: texteValeur(p, d[p.cle]), etape: p.etape })
  })
  // chiffrage au matériel : ratios et marges saisis (vides = catalogue)
  const c = catalogueEtude.choix
  if (s.chiffrageMateriel && s.materiel && c) {
    const m = margesMinimales(s, s.pvRangeMax) || {}
    const materiel: any[] = [
      { cle: 'ratioReseau', libelle: 'Ratio panneaux / onduleur réseau', defaut: c.ratios && c.ratios.reseau, si: s.clientType !== 'Particulier' },
      { cle: 'ratioHybride', libelle: 'Ratio panneaux / onduleur hybride', defaut: c.ratios && c.ratios.hybride, si: true },
    ]
    SECTIONS_MARGE.forEach(x => materiel.push({ cle: x.cle, libelle: x.libelle, defaut: m[x.catalogue], unite: '%', si: true }))
    materiel.forEach(m => {
      const v = s.materiel[m.cle]
      if (m.si && v != null && v !== '' && estModifie(v, m.defaut)) {
        ecarts.push({ libelle: m.libelle, valeur: texteValeur(null, v, m.unite || ''), defaut: texteValeur(null, m.defaut, m.unite || ''), etape: 4 })
      }
    })
  }
  return ecarts
}
